import { useState, useEffect } from 'react';
import { CheckCircle2, Clock, Radio, TrendingUp, XCircle, Pause, HelpCircle, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CampaignStatusTracker = ({ onNavigate }) => {
  const [campaignData, setCampaignData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isHidden, setIsHidden] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has hidden the tracker
    const hidden = localStorage.getItem('campaignTrackerHidden');
    if (hidden === 'true') {
      setIsHidden(true);
    }
  }, []);

  const handleClose = () => {
    setIsHidden(true);
    localStorage.setItem('campaignTrackerHidden', 'true');
  };

  const statusConfig = {
    neu: {
      step: 2,
      icon: Clock,
      label: 'Wird überprüft',
      color: 'text-yellow-600',
      description: 'Wir überprüfen Ihre Kampagne',
      nextStep: 'Live schalten',
      animated: true,
      showClose: true
    },
    in_bearbeitung: {
      step: 2,
      icon: Clock,
      label: 'Wird überprüft',
      color: 'text-yellow-600',
      description: 'Wir überprüfen Ihre Kampagne',
      nextStep: 'Live schalten',
      animated: true,
      showClose: true
    },
    aktiv: {
      step: 3,
      icon: CheckCircle2,
      label: 'Live',
      color: 'text-green-600',
      description: 'Ihre Suchanzeige ist live und liefert Ergebnisse',
      nextStep: null,
      showClose: true
    },
    paused: {
      step: 0,
      icon: Pause,
      label: 'Pausiert',
      color: 'text-gray-600',
      description: 'Ihre Kampagne ist pausiert',
      nextStep: 'Im Anzeigen-Tab wieder aktivieren',
      showClose: true
    },
    rejected: {
      step: 0,
      icon: XCircle,
      label: 'Abgelehnt',
      color: 'text-red-600',
      description: 'Ihre Kampagne wurde abgelehnt',
      nextStep: 'Support kontaktieren für Unterstützung',
      showSupport: true,
      showClose: true
    }
  };

  const steps = [
    { id: 1, icon: CheckCircle2, label: 'Anzeige erstellt', mobileLabel: 'Erstellt' },
    { id: 2, icon: Clock, label: 'Wird geprüft', mobileLabel: 'Prüfung' },
    { id: 3, icon: Radio, label: 'Live geschaltet', mobileLabel: 'Live' },
    { id: 4, icon: TrendingUp, label: 'Erste Ergebnisse', mobileLabel: 'Ergebnisse' }
  ];

  useEffect(() => {
    fetchCampaignStatus();
    // Auto-refresh alle 30 Minuten
    const interval = setInterval(fetchCampaignStatus, 1800000); // 30 min
    return () => clearInterval(interval);
  }, []);

  const fetchCampaignStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Hole Kampagnen
      const campaignsResponse = await axios.get(`${API}/campaigns/my-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!campaignsResponse.data || campaignsResponse.data.length === 0) {
        setCampaignData(null);
        setLoading(false);
        return;
      }

      // Sortiere nach created_at und hole neueste
      const sortedCampaigns = campaignsResponse.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      const latestCampaign = sortedCampaigns[0];

      setCampaignData(latestCampaign);

      // Hole Stats für "Erste Ergebnisse" Check
      const statsResponse = await axios.get(`${API}/dashboard/kpis`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStatsData(statsResponse.data);

    } catch (error) {
      console.error('Error fetching campaign status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStep = () => {
    if (!campaignData) return 0;

    const status = campaignData.status;
    const config = statusConfig[status];

    // Sonderstatus (paused, rejected)
    if (config.step === 0) return config.step;

    // Normal flow
    if (status === 'aktiv' && statsData?.total_clicks > 0) {
      return 4; // Erste Ergebnisse
    }

    return config.step;
  };

  const handleNavigateToAds = () => {
    if (onNavigate) {
      onNavigate('anzeigen');
    }
  };

  const handleNavigateToResults = () => {
    if (onNavigate) {
      onNavigate('ergebnisse');
    }
  };

  const handleNavigateToHelp = () => {
    if (onNavigate) {
      onNavigate('hilfe');
    }
  };

  // If user has closed the tracker, don't show it
  if (isHidden) {
    return null;
  }

  if (loading) {
    return (
      <div className="p-6 border border-gray-200 rounded-xl bg-white animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    );
  }

  // Keine Kampagne = Zeige "Jetzt erstellen" CTA
  if (!campaignData) {
    return (
      <div className="p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
        <div className="text-center py-8">
          <div className="mb-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto">
              <Radio className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Noch keine Kampagne erstellt
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            Starten Sie jetzt und werden Sie sichtbar für Ihre Kunden
          </p>
          <Button
            onClick={handleNavigateToAds}
            className="bg-[#0040FF] hover:bg-[#0033CC] text-white"
          >
            Jetzt Sichtbar werden
          </Button>
        </div>
      </div>
    );
  }

  const currentStep = getCurrentStep();
  const status = campaignData.status;
  const config = statusConfig[status];
  const StatusIcon = config.icon;

  // Zeit seit Erstellung
  const createdDate = new Date(campaignData.created_at);
  const hoursAgo = Math.floor((Date.now() - createdDate) / (1000 * 60 * 60));

  return (
    <div className="p-5 border border-gray-200 rounded-xl bg-white relative">
      {/* Close Button (X) */}
      {config.showClose && (
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Schließen"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      )}

      {/* Simple Status Display */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 pr-8">
        <div className="flex items-center gap-3">
          <StatusIcon className={`w-6 h-6 ${config.color} flex-shrink-0 ${config.animated ? 'animate-pulse' : ''}`} />
          <div>
            <p className="text-sm font-medium text-gray-900 text-sharp">
              {config.description}
            </p>
            {config.nextStep && (
              <p className="text-xs text-gray-500 mt-0.5">
                Nächster Schritt: {config.nextStep}
              </p>
            )}
          </div>
        </div>

        {/* Button inline auf Desktop (wenn aktiv + clicks > 0) */}
        {currentStep === 4 && statsData && statsData.total_clicks > 0 && (
          <Button
            onClick={handleNavigateToResults}
            variant="outline"
            size="sm"
            className="w-full md:w-auto md:ml-auto border-gray-300 text-gray-700 hover:bg-gray-50 text-xs"
          >
            Ergebnisse ansehen
            <ArrowRight className="w-3 h-3 ml-1.5" />
          </Button>
        )}
      </div>

      {/* Support Button nur bei Rejection */}
      {config.showSupport && (
        <div className="mt-4">
          <Button 
            size="sm"
            className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white"
            onClick={handleNavigateToHelp}
          >
            Support kontaktieren
          </Button>
        </div>
      )}
    </div>
  );
};

export default CampaignStatusTracker;
